import { useEffect, useState } from 'react';
import { mdiClose, mdiPlus, mdiTrashCanOutline } from '@mdi/js';
import Icon from '@mdi/react';
import {
  Button,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Table,
} from 'reactstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from './constants';
import './NewFamilyDetailsModal.scss';
import { DatePicker } from '../../Common';

function NewFamilyDetailsModal({
  isEdit,
  isOpen,
  toggle,
  setValue,
  detailsData,
}) {
  const familyInformation = detailsData?.travelerDetails;
  const headerDetails = detailsData?.headerDetails;
  const [isEditable, setIsEditable] = useState(true);

  useEffect(() => {
    if (!isEdit) {
      setIsEditable(true);
    } else if (headerDetails?.Status !== 'New') {
      setIsEditable(false);
    } else {
      setIsEditable(true);
    }
  }, [headerDetails]);

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      family: [],
    },
  });

  const onSubmit = async data => {
    setValue('familyInformation', data?.family);
    toggle();
  };

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'family',
  });

  const removeRow = index => {
    if (fields.length > 1) {
      remove(index);
    } else {
      replace({});
    }
  };

  useEffect(() => {
    if (
      isEdit &&
      familyInformation &&
      Array.isArray(familyInformation) &&
      familyInformation.length > 0
    ) {
      // Map API fields to form fields
      const mapped = familyInformation.map(item => ({
        relationship: item?.Relationship?.trim(),
        dob: item?.DOB?.trim()?.split('T')?.[0],
        passport: item?.PassportNo?.trim(),
        lastName: item?.Traveler_LName?.trim(),
        middleName: item?.Traveler_MName?.trim(),
        firstName: item?.Traveler_FName?.trim(),
      }));
      replace(mapped);
    } else if (fields.length === 0) {
      append({});
    }
  }, [familyInformation, replace, append, fields.length, isEdit]);

  const renderTableBody = () =>
    fields.map((field, index) => (
      <tr key={field.id}>
        <td className="text-center px-2">{index + 1}.</td>
        <td className="py-1 px-2 family-field">
          <Controller
            control={control}
            name={`family.${index}.firstName`}
            render={({ field: element }) => (
              <Input
                {...element}
                placeholder="First Name"
                className="border-0 p-0"
                readOnly={!isEditable}
              />
            )}
          />
          {errors.family?.[index]?.firstName && (
            <div className="text-danger">
              {errors.family[index].firstName.message}
            </div>
          )}
        </td>
        <td className="py-1 px-2 family-field">
          <Controller
            control={control}
            name={`family.${index}.middleName`}
            render={({ field: element }) => (
              <Input
                {...element}
                placeholder="Middle Name"
                className="border-0 p-0"
                readOnly={!isEditable}
              />
            )}
          />
        </td>
        <td className="py-1 px-2 family-field">
          <Controller
            control={control}
            name={`family.${index}.lastName`}
            render={({ field: element }) => (
              <Input
                {...element}
                placeholder="Last Name"
                className="border-0 p-0"
                readOnly={!isEditable}
              />
            )}
          />
          {errors.family?.[index]?.lastName && (
            <div className="text-danger">
              {errors.family[index].lastName.message}
            </div>
          )}
        </td>
        <td className="py-1 px-2 family-field">
          <Controller
            control={control}
            name={`family.${index}.dob`}
            render={({ field: element }) => (
              <DatePicker
                {...element}
                className="border-0"
                readOnly={!isEditable}
              />
            )}
          />
          {errors.family?.[index]?.dob && (
            <div className="text-danger">
              {errors.family[index].dob.message}
            </div>
          )}
        </td>
        <td className="py-1 px-2 family-field">
          <Controller
            control={control}
            name={`family.${index}.passport`}
            render={({ field: element }) => (
              <Input
                {...element}
                placeholder="Passport No."
                className="border-0 p-0"
                readOnly={!isEditable}
              />
            )}
          />
          {errors.family?.[index]?.passport && (
            <div className="text-danger">
              {errors.family[index].passport.message}
            </div>
          )}
        </td>
        <td className="py-1 px-2 family-field">
          <Controller
            control={control}
            name={`family.${index}.relationship`}
            render={({ field: element }) => (
              <Input
                {...element}
                placeholder="Relationship"
                className="border-0 p-0"
                readOnly={!isEditable}
              />
            )}
          />
        </td>
        <td className="py-1 px-2 text-center">
          {index >= 0 && isEditable && (
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => removeRow(index)}
            >
              <Icon path={mdiTrashCanOutline} size={0.7} color="#D92D20" />
            </button>
          )}
        </td>
      </tr>
    ));

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <div className="modal-header p-0">
        <div className="py-2 px-3 d-flex flex-row align-items-center justify-content-between w-100">
          <div className="h5 mb-0">Family Details</div>
          <div className="d-flex flex-row align-items-center gap-2">
            {isEditable && (
              <button
                type="button"
                className="custom-secondary-btn"
                onClick={() => append({})}
              >
                <Icon path={mdiPlus} size={0.5} />
                <span className="fw-semibold">Add New</span>
              </button>
            )}

            <button className="btn btn-link" type="button" onClick={toggle}>
              <Icon path={mdiClose} size={0.8} />
            </button>
          </div>
        </div>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)} className="ps-3 pe-3">
        <ModalBody className="p-0 pb-3">
          <div className="table-responsive">
            <Table
              className="ba-option-table align-middle text-nowrap"
              id="family-table"
            >
              <thead>
                <tr>
                  <th className="text-center">#</th>
                  <th>
                    First Name <span className="text-danger">*</span>
                  </th>
                  <th>Middle Name</th>
                  <th>
                    Last Name <span className="text-danger">*</span>
                  </th>
                  <th>
                    Date of Birth <span className="text-danger">*</span>
                  </th>
                  <th>
                    Passport No. <span className="text-danger">*</span>
                  </th>
                  <th>Relationship</th>
                  <th> </th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </ModalBody>
        {isEditable && (
          <ModalFooter>
            <Button
              type="button"
              className="custom-btn"
              onClick={async () => {
                const isFormValid = await trigger();
                if (!isFormValid) {
                  return;
                }
                handleSubmit(onSubmit)();
              }}
            >
              Submit
            </Button>
          </ModalFooter>
        )}
      </Form>
    </Modal>
  );
}

export default NewFamilyDetailsModal;
